// src/hooks/useDispositionAccueil.ts — SCE Issues
// Mon accueil, mes files (chantier du 03/11, docs/chantier-accueil.md) : la
// disposition de l'accueil de la personne affichée = le modèle de son rôle + ses
// différences (bloc `dispositionAccueil` de Prefs), selon ses droits.
//
// Accueil personnalisable fermé (essai) et personne autre que l'administrateur :
// rien n'est lu, le modèle du code — l'accueil d'avant le chantier.
// Bascule de rôle : on lit la disposition de la personne regardée, on n'écrit
// rien (useReglagesBloc) ; en bascule, le modèle de /admin s'applique même pendant
// l'essai — l'administrateur voit ainsi l'accueil que son modèle donnera.
//
// Lot 6 : le modèle du rôle est celui de /admin › « Accueil par rôle » s'il y en
// a un (useModelesAccueil). Une carte ou une file arrivée dans le modèle est
// marquée « Nouveau » le temps de la visite, puis comptée comme vue (connues).

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useModelesAccueil } from './useModelesAccueil'
import { accueilPersonnalisable } from '@/config/accueil.config'
import { estAdmin } from '@/config/admin.config'
import type { VuePerso } from '@/utils/tableauProblemes'
import { useReglagesBloc } from './useReglagesBloc'
import { useCurrentUser } from './useCurrentUser'
import {
  differences,
  disposer,
  epinglerVue,
  vueEpinglee,
  lireDifferences,
  MODELES_ACCUEIL_DEFAUT,
  modeleEffectif,
  type DifferencesAccueil,
  type Disposition,
  type ModeleAccueil,
  type Rangement,
  type RoleAccueil,
} from '@/utils/dispositionAccueil'

/** Le bloc tel qu'il est rangé : une enveloppe, pour pouvoir enregistrer « aucune différence ». */
interface BlocDisposition {
  diff: DifferencesAccueil | null
}

function lireBloc(brut: unknown): BlocDisposition {
  const diff = brut && typeof brut === 'object' && 'diff' in brut ? (brut as { diff: unknown }).diff : undefined
  return { diff: lireDifferences(diff) }
}

export interface UseDispositionAccueil {
  disposition: Disposition
  modele: ModeleAccueil
  /** La personne peut ranger son accueil (essai : l'administrateur), hors bascule de rôle. */
  modifiable: boolean
  /** A-t-elle des différences avec le modèle ? (« Revenir au modèle ») */
  personnalisee: boolean
  /** Enregistre une disposition rangée à la main (seules les différences sont gardées). */
  enregistrer: (d: Rangement) => void
  revenirAuModele: () => void
  /** Cartes et files arrivées du modèle : « Nouveau » le temps de cette visite. */
  nouvelles: ReadonlySet<string>
}

export function useDispositionAccueil(role: RoleAccueil, disponible: (id: string) => boolean): UseDispositionAccueil {
  const { user, enVueRole, isFournisseur } = useCurrentUser()
  // En bascule de rôle (l'administrateur en production) : lu, jamais écrit.
  // Un fournisseur garde l'accueil du code (lot 8).
  const actif = !isFournisseur && (accueilPersonnalisable(user?.upn) || enVueRole)
  const { reglages, modifier, chargement } = useReglagesBloc('dispositionAccueil', 'sce-issues.disposition-accueil', lireBloc, '', actif)
  const { modeles, charge } = useModelesAccueil(actif)
  const enregistre = modeles[role]
  const modele = useMemo(() => modeleEffectif(MODELES_ACCUEIL_DEFAUT[role], enregistre), [role, enregistre])
  const diff = actif ? reglages.diff : null
  const disposition = useMemo(() => disposer(modele, diff, disponible), [modele, diff, disponible])
  const modifiable = actif && !enVueRole

  const enregistrer = useCallback(
    // Le téléphone se range à part (lot 7) : un rangement du bureau garde le sien.
    (d: Rangement) => modifier(() => ({ diff: differences({ ...d, telephone: d.telephone ?? disposition.telephone }, modele, disponible) })),
    [modifier, modele, disponible, disposition.telephone],
  )
  const revenirAuModele = useCallback(() => modifier(() => ({ diff: null })), [modifier])

  // « Nouveau » : gardé pour la visite, puis la disposition est réenregistrée
  // telle qu'affichée — ses cartes du modèle deviennent « connues ».
  const [nouvelles, setNouvelles] = useState<ReadonlySet<string>>(new Set())
  const aReconnaitre = modifiable && !chargement && charge && disposition.nouvelles.length > 0
  useEffect(() => {
    if (!aReconnaitre) return
    setNouvelles((p) => new Set([...p, ...disposition.nouvelles]))
    enregistrer(disposition)
  }, [aReconnaitre, disposition, enregistrer])

  return {
    disposition,
    modele,
    modifiable,
    personnalisee: diff !== null,
    enregistrer,
    revenirAuModele,
    nouvelles,
  }
}

// ─── « Épingler à l'accueil » depuis /issues (lot 5) ───

export interface UseEpinglerAccueil {
  /** La personne a un accueil de coordinateur personnalisable (essai : l'administrateur), hors bascule de rôle. */
  peut: boolean
  estEpinglee: (vueId: string) => boolean
  /** Ajoute la vue en file liée ; renvoie la raison d'un refus, sinon null. */
  epingler: (vue: VuePerso) => string | null
}

export function useEpinglerAccueil(): UseEpinglerAccueil {
  const { user, isCoordinateur, enVueRole } = useCurrentUser()
  const admin = !!user && estAdmin(user.upn)
  const peut = !enVueRole && (isCoordinateur || admin) && accueilPersonnalisable(user?.upn)
  const { reglages, modifier } = useReglagesBloc('dispositionAccueil', 'sce-issues.disposition-accueil', lireBloc, '', peut)
  // Même modèle que l'accueil (HomePage : un coordinateur a ses métiers, l'administrateur tous).
  const role: RoleAccueil = isCoordinateur ? 'coordinateur' : 'administrateur'
  const { modeles } = useModelesAccueil(peut)
  const enregistre = modeles[role]
  const modele = useMemo(() => modeleEffectif(MODELES_ACCUEIL_DEFAUT[role], enregistre), [role, enregistre])
  const estEpinglee = useCallback((vueId: string) => vueEpinglee(reglages.diff, vueId), [reglages.diff])
  const epingler = useCallback(
    (vue: VuePerso): string | null => {
      const r = epinglerVue(reglages.diff, modele, vue)
      if (r.erreur) return r.erreur
      modifier(() => ({ diff: r.diff }))
      return null
    },
    [reglages.diff, modele, modifier],
  )
  return { peut, estEpinglee, epingler }
}
